import { z } from "zod";
import { dashboardBindingReview } from "./fermentation-dashboard-binding-review";
const reference = z.string().trim().min(1);
const references = z.array(reference);
const label = z.string().nullable().default(null);
const emphasis = z.enum(["small", "normal", "prominent"]);
const bounds = z
    .object({
    low: z.number().finite().nullable(),
    high: z.number().finite().nullable(),
})
    .strict();
export const dashboardReadoutRange = z
    .object({
    enabled: z.boolean(),
    /** The unit of the projected value, after registry conversion. */
    unit: reference,
    warning: bounds,
    danger: bounds,
    maxAgeS: z.number().finite().positive(),
})
    .strict()
    .superRefine((range, ctx) => {
    const thresholds = [
        range.danger.low,
        range.warning.low,
        range.warning.high,
        range.danger.high,
    ].filter((value) => value !== null);
    if (thresholds.some((value, i) => i > 0 && value <= thresholds[i - 1])) {
        ctx.addIssue({
            code: "custom",
            message: "Display thresholds must increase from danger low to danger high.",
        });
    }
});
/**
 * A widget is a rendering primitive bound to tags. Nothing here names a
 * biological role or a controller type; the same widget set renders a pump
 * feed, a thermal loop and an external PLC signal.
 */
export const dashboardWidget = z.discriminatedUnion("kind", [
    z
        .object({
        kind: z.literal("readout"),
        tag: reference,
        label,
        emphasis: emphasis.default("normal"),
        /**
         * The controller this reading belongs to when it is not the card's: its
         * setpoint is edited here, and an output it drives is handed to manual here.
         */
        controller: reference.optional(),
    })
        .strict(),
    z.object({ kind: z.literal("reservoir"), tag: reference, label }).strict(),
    z
        .object({
        kind: z.literal("signed-bar"),
        tag: reference,
        negativeLabel: z.string(),
        positiveLabel: z.string(),
    })
        .strict(),
    z
        .object({
        kind: z.literal("cascade"),
        demandTag: reference,
        stages: z
            .array(z
            .object({
            tag: reference,
            label: z.string(),
            /** Key in the controller's tuning document holding the stage profile. */
            tuningKey: reference,
        })
            .strict())
            .min(1),
    })
        .strict(),
    /** Decoded through the signal's enum labels from the controller catalog. */
    z.object({ kind: z.literal("enum-status"), tag: reference, label }).strict(),
    /** Several readings on one line; a setpoint reading renders editable. */
    z
        .object({
        kind: z.literal("row"),
        items: z
            .array(z
            .object({
            tag: reference,
            label,
            /** As on a readout: the controller this reading belongs to when it is not the card's. */
            controller: reference.optional(),
        })
            .strict())
            .min(1),
        /** Small rows are one line of "label value" pairs side by side. */
        emphasis: z.enum(["small", "normal"]).default("normal"),
    })
        .strict(),
    /** One line whose text follows an enum reading, optionally with a companion value. */
    z
        .object({
        kind: z.literal("status"),
        tag: reference,
        cases: z
            .array(z
            .object({
            /** Enum label of the reading this case applies to. */
            variant: reference,
            text: z.string(),
            tag: reference.nullable().default(null),
        })
            .strict())
            .min(1),
    })
        .strict(),
    /** Catalog action names offered as buttons. */
    z.object({ kind: z.literal("actions"), names: references.min(1) }).strict(),
    /** Starts a titled lane: the widgets after it, up to the next lane, are drawn as one section. */
    z.object({ kind: z.literal("lane"), title: z.string().trim().min(1) }).strict(),
]);
export const dashboardCardIcon = z
    .object({
    /** Open set; the renderer draws a vessel for a glyph it does not know. */
    glyph: reference.default("vessel"),
    animation: z
        .object({ kind: z.literal("rotate"), tag: reference })
        .strict()
        .nullable()
        .default(null),
})
    .strict()
    .default({});
const blockFields = {
    id: reference,
    title: z.string().nullable().default(null),
    groupId: reference,
};
export const dashboardCard = z
    .object({
    ...blockFields,
    type: z.literal("card"),
    color: reference.nullable().default(null),
    /** Null is a signal card: no mode chip, no controls, no actions. */
    controller: reference.nullable().default(null),
    /** Feed line code this card belongs to, for colour and record links. */
    line: reference.nullable().default(null),
    /** Tile width: s packs five per row, m three, l two, xl one. */
    size: z.enum(["s", "m", "l", "xl"]).default("m"),
    icon: dashboardCardIcon,
    headline: z
        .object({
        tag: reference,
        targetTag: reference.nullable().default(null),
        /** A small companion under the headline number: an error, a read-back speed. */
        detailTag: reference.nullable().default(null),
        detailLabel: z.string().nullable().default(null),
        /** Always print the sign, so a difference reads as one. */
        detailSigned: z.boolean().default(false),
        label,
    })
        .strict()
        .nullable()
        .default(null),
    widgets: z.array(dashboardWidget).default([]),
    /** Keyed by a displayed tag: headline, target or a widget tag. */
    readoutRanges: z.record(reference, dashboardReadoutRange).default({}),
    /**
     * Keyed by a displayed tag: the unit this card shows it in and takes it
     * typed in, when the procedure's display unit reads badly here -- a dose
     * interval in minutes, a vessel in kilograms. Presentation only: the node
     * and the parameter keep their own units.
     */
    displayUnits: z.record(reference, reference).default({}),
    /**
     * Keyed by a displayed setpoint tag: the parameter an operator edits to
     * move it, when that is not the setpoint itself.
     *
     * A controller's setpoint is sometimes derived from a parameter the
     * controller never sees -- a feed rate ruled from a specific rate, say --
     * and then the number the node holds is not the number anyone decides.
     * Editing the setpoint in that case cuts the rule, so what the card offers
     * is the parameter upstream of it. `parameter` is a resolver name, not a
     * tag: the handle is deliberately not something the controller consumes,
     * so it need not be a port, and may not be in the node's vocabulary.
     *
     * What is edited and what is read are separate questions. The knob is the
     * knob whichever number a card puts in front of it, so `display` only
     * moves the readout, and defaults to the setpoint -- the quantity the
     * equipment is actually working in.
     */
    handles: z
        .record(reference, z.preprocess(
    // A stored revision may predate the display choice, when naming the
    // parameter meant reading it too. Revisions are restorable, so the
    // old spelling has to keep parsing -- and keep meaning what it did.
    (value) => typeof value === "string"
        ? { parameter: value, display: "parameter" }
        : value, z
        .object({
        parameter: reference,
        display: z.enum(["setpoint", "parameter"]).default("setpoint"),
    })
        .strict()))
        .default({}),
})
    .strict();
/** A live window: `15m`, `6h`, `3d`, `1w`, or `all` for the whole run. */
export const plotWindow = z.string().regex(/^(\d+[mhdw]|all)$/);
export const dashboardPlotBlockV2 = z
    .object({
    ...blockFields,
    type: z.literal("plot"),
    attributes: references.default([]),
    /** Default live window; operators switch windows for the session without changing it. */
    window: plotWindow.default("6h"),
    /**
     * Drawn height. The plot fills the slot; the stack scrolls when it must.
     * `screen` is the height of the window, so a scrolled-to trend fills it.
     */
    height: z.enum(["s", "m", "l", "xl", "screen"]).default("m"),
    primary: z.boolean().default(false),
    /** Attribute identity survives reordering and repeated cards with different colours. */
    seriesPresentation: z
        .record(reference, z
        .object({
        color: reference.nullable().default(null),
        /** A target inherits the paired measurement's colour and uses a dashed line. */
        measurementAttribute: reference.nullable().default(null),
        /** A dedicated y axis even when another series shares the unit. */
        ownAxis: z.boolean().default(false),
        /**
         * A named y axis shared by every series that names it, whatever their
         * units: stirrer speed and airflow on one 0–800 scale, say. It wins
         * over unit grouping and over `ownAxis`, and labels the axis.
         */
        axis: z.string().trim().min(1).nullable().default(null),
        /** Fixed axis bounds; null is auto-range. The first series on an axis sets them. */
        yMin: z.number().finite().nullable().default(null),
        yMax: z.number().finite().nullable().default(null),
        log: z.boolean().default(false),
    })
        .strict())
        .default({}),
})
    .strict();
const block = z.discriminatedUnion("type", [
    dashboardCard,
    dashboardPlotBlockV2,
]);
/** The controllers a card's readouts belong to besides its own. */
export function widgetControllers(card) {
    return [
        ...new Set(card.widgets.flatMap((widget) => widget.kind === "readout"
            ? widget.controller
                ? [widget.controller]
                : []
            : widget.kind === "row"
                ? widget.items.flatMap((item) => (item.controller ? [item.controller] : []))
                : [])),
    ];
}
/**
 * The card's controllers, its own first, each with the title of the lane it
 * first appears in (null outside a lane). Tuning a card tunes all of them.
 */
export function cardControllers(card) {
    const found = new Map();
    if (card.controller)
        found.set(card.controller, null);
    let lane = null;
    for (const widget of card.widgets) {
        if (widget.kind === "lane")
            lane = widget.title;
        const names = widget.kind === "readout"
            ? [widget.controller]
            : widget.kind === "row"
                ? widget.items.map((item) => item.controller)
                : [];
        for (const name of names)
            if (name && !found.has(name))
                found.set(name, lane);
    }
    return [...found].map(([controller, title]) => ({ controller, lane: title }));
}
/** Every tag a card reads, in display order. The single source for ranges, snapshots and bindings. */
export function displayedTags(card) {
    const tags = [];
    if (card.headline) {
        tags.push(card.headline.tag);
        if (card.headline.targetTag)
            tags.push(card.headline.targetTag);
        if (card.headline.detailTag)
            tags.push(card.headline.detailTag);
    }
    for (const widget of card.widgets) {
        switch (widget.kind) {
            case "readout":
            case "reservoir":
            case "signed-bar":
            case "enum-status":
                tags.push(widget.tag);
                break;
            case "row":
                tags.push(...widget.items.map((item) => item.tag));
                break;
            case "status":
                tags.push(widget.tag, ...widget.cases.flatMap((entry) => (entry.tag ? [entry.tag] : [])));
                break;
            case "cascade":
                tags.push(widget.demandTag, ...widget.stages.map((stage) => stage.tag));
                break;
            case "actions":
            case "lane":
                break;
        }
    }
    return [...new Set(tags)];
}
export const fermentationDashboardLayoutV2 = z
    .object({
    kind: z.literal("fermentation"),
    version: z.literal(2),
    groups: z.array(z
        .object({
        id: reference,
        title: z.string().nullable().default(null),
        layout: z.enum(["tiles", "strip", "plots"]),
    })
        .strict()),
    blocks: z.array(block),
    /** Assigned by the server after reviewing the resolved reference inventory. */
    reviewedBindings: dashboardBindingReview.nullable().default(null),
})
    .strict()
    .superRefine((layout, ctx) => {
    const issue = (path, message) => ctx.addIssue({ code: "custom", path, message });
    const unique = (values, path) => {
        const seen = new Set();
        values.forEach((value, i) => {
            if (seen.has(value))
                issue([...path, i], `Duplicate reference: ${value}.`);
            seen.add(value);
        });
    };
    unique(layout.groups.map((group) => group.id), ["groups"]);
    unique(layout.blocks.map((entry) => entry.id), ["blocks"]);
    const groups = new Map(layout.groups.map((group) => [group.id, group]));
    let primaryCount = 0;
    layout.blocks.forEach((entry, i) => {
        const path = ["blocks", i];
        const group = groups.get(entry.groupId);
        if (!group)
            issue([...path, "groupId"], "The selected group does not exist.");
        else if ((entry.type === "plot") !== (group.layout === "plots")) {
            issue([...path, "groupId"], "Plot groups accept only plots; tiles and strips accept cards.");
        }
        if (entry.type === "plot") {
            if (entry.primary && ++primaryCount > 1)
                issue([...path, "primary"], "Only one plot can be primary.");
            unique(entry.attributes, [...path, "attributes"]);
            for (const [attribute, style] of Object.entries(entry.seriesPresentation)) {
                const stylePath = [...path, "seriesPresentation", attribute];
                if (!entry.attributes.includes(attribute))
                    issue(stylePath, "Series presentation must reference a selected attribute.");
                const measurement = style.measurementAttribute;
                if (measurement !== null &&
                    (measurement === attribute ||
                        !entry.attributes.includes(measurement) ||
                        entry.seriesPresentation[measurement]?.measurementAttribute !=
                            null))
                    issue(stylePath, "A target must pair with a selected measurement, not another target.");
            }
            return;
        }
        const displayed = new Set(displayedTags(entry));
        if (entry.headline === null && entry.widgets.length === 0)
            issue([...path, "widgets"], "A card without a headline needs a widget.");
        if (entry.headline && entry.headline.targetTag === entry.headline.tag)
            issue([...path, "headline", "targetTag"], "The target must differ from the headline.");
        if (entry.headline && entry.headline.detailTag === entry.headline.tag)
            issue([...path, "headline", "detailTag"], "The detail must differ from the headline.");
        if (entry.icon.animation !== null &&
            !displayed.has(entry.icon.animation.tag))
            issue([...path, "icon", "animation"], "Animation must follow a displayed reading.");
        if (entry.controller === null) {
            entry.widgets.forEach((widget, j) => {
                if (widget.kind === "actions" || widget.kind === "cascade")
                    issue([...path, "widgets", j], "A signal card cannot carry controller widgets.");
            });
        }
        for (const tag of Object.keys(entry.readoutRanges)) {
            if (!displayed.has(tag))
                issue([...path, "readoutRanges", tag], "Ranges must reference a displayed reading.");
        }
        for (const tag of Object.keys(entry.displayUnits)) {
            if (!displayed.has(tag))
                issue([...path, "displayUnits", tag], "Display units must reference a displayed reading.");
        }
        // Whether the tag is a setpoint the card may write is a question about
        // the controller, so it belongs to the binding review; all that can be
        // said here is that nothing on the card shows it.
        for (const tag of Object.keys(entry.handles)) {
            if (!displayed.has(tag))
                issue([...path, "handles", tag], "Handles must reference a displayed reading.");
        }
    });
});
//# sourceMappingURL=fermentation-dashboard-v2.js.map